import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { UserInput } from '../types';

class UserService {
  // Get all users
  async getUsers() {
    const query = gql`
      query GetUsers {
        users {
          id
          username
          emailAddress
          firstName
          lastName
          displayName
          languageCode
          identityProvider
          linkId
          roleId
          mappings {
            tenantId
            roleId
          }
          createdDate
          isActive
          lastLoginDate
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL for users at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL users response:', result);
      return result.data.users;
    } catch (error: any) {
      console.error('Error fetching users:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch users: ${error.message}`);
      }
    }
  }

  // Get roles filtered by current user's role (backend enforced security)
  async getFilteredRoles() {
    const query = gql`
      query GetFilteredRoles {
        filteredRoles {
          id
          roleId
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL for filtered roles at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL filtered roles response:', result);
      
      // Ensure we always return an array, never null or undefined
      return result.data.filteredRoles || [];
    } catch (error: any) {
      console.error('Error fetching filtered roles:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch roles: ${error.message}`);
      }
    }
  }

  // Get users filtered by current user's role (backend enforced security)
  async getFilteredUsers() {
    const query = gql`
      query GetFilteredUsers {
        filteredUsers {
          id
          username
          emailAddress
          firstName
          lastName
          displayName
          languageCode
          identityProvider
          linkId
          roleId
          mappings {
            tenantId
            roleId
          }
          createdDate
          isActive
          lastLoginDate
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL for filtered users at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL filtered users response:', result);
      
      // Ensure we always return an array, never null or undefined
      return result.data.filteredUsers || [];
    } catch (error: any) {
      console.error('Error fetching filtered users:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch filtered users: ${error.message}`);
      }
    }
  }

  // Get user by ID
  async getUserById(id: string) {
    const query = gql`
      query GetUser($id: String!) {
        user(id: $id) {
          id
          username
          emailAddress
          firstName
          lastName
          displayName
          languageCode
          identityProvider
          linkId
          roleId
          mappings {
            tenantId
            roleId
          }
          createdDate
          isActive
          lastLoginDate
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return result.data.user;
    } catch (error: any) {
      console.error('Error fetching user by ID:', error);
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch user: ${error.message}`);
      }
    }
  }

  // Create new user
  async createUser(userInput: UserInput) {
    const mutation = gql`
      mutation CreateUser($input: UserInput!) {
        createUser(input: $input) {
          id
          username
          emailAddress
          firstName
          lastName
          displayName
          languageCode
          identityProvider
          linkId
          roleId
          mappings {
            tenantId
            roleId
          }
          createdDate
          isActive
          lastLoginDate
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { input: userInput }
      });
      return result.data.createUser;
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to create user: ${error.message}`);
      }
    }
  }

  // Update user
  async updateUser(id: string, userInput: UserInput) {
    const mutation = gql`
      mutation UpdateUser($id: String!, $input: UserInput!) {
        updateUser(id: $id, input: $input) {
          id
          username
          emailAddress
          firstName
          lastName
          displayName
          languageCode
          identityProvider
          linkId
          roleId
          mappings {
            tenantId
            roleId
          }
          createdDate
          isActive
          lastLoginDate
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, input: userInput }
      });
      return result.data.updateUser;
    } catch (error: any) {
      console.error('Error updating user:', error);
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to update user: ${error.message}`);
      }
    }
  }

  // Delete user
  async deleteUser(id: string) {
    const mutation = gql`
      mutation DeleteUser($id: String!) {
        deleteUser(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.deleteUser;
    } catch (error: any) {
      console.error('Error deleting user:', error);
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to delete user: ${error.message}`);
      }
    }
  }
}

export default new UserService();
